import api from './api';
import { ClipboardItem, createItem } from '../models';
import { storage } from './storage';

// Cache API helper function
const getContentCache = async () => {
  const cacheName = 'clipboard-content-v1';
  return await caches.open(cacheName);
};

// Remove query parameters from URL for cache key
const getCacheKey = (url) => {
  try {
    const urlObj = new URL(url);
    urlObj.search = ''; // Remove query parameters
    return urlObj.toString();
  } catch (error) {
    // If URL parsing fails, return original URL
    return url;
  }
};

// Preload content for a single item
const preloadItemContent = async (item, cache) => {
  // Skip if no contentUrl
  if (!item.contentUrl) {
    return;
  }

  try {
    // Get cache key without query parameters
    const cacheKey = getCacheKey(item.contentUrl);
    
    // Check if already cached
    const cachedResponse = await cache.match(cacheKey);
    
    if (cachedResponse) {
      console.log('Already cached', cacheKey);
      // Create blob URL from cached content
      const blob = await cachedResponse.blob();
      const blobUrl = URL.createObjectURL(blob);
      item.contentUrl = blobUrl;
      return; // Already cached, skip
    }

    // Fetch content from URL (with original query parameters if needed)
    const response = await fetch(item.contentUrl);
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    // Clone response before caching to avoid "body already read" errors
    const responseClone = response.clone();

    // Store in cache using cleaned URL (without query parameters)
    await cache.put(cacheKey, responseClone);

    // Create blob URL from fetched content
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    item.contentUrl = blobUrl;
  } catch (error) {
    // Log error but don't throw - preloading failures are non-critical
    console.warn(`Failed to preload content for item ${item.id}:`, error);
  }
};

export const folderService = {

  async getFolders() {
    const response = await api.get('/api/clipboard');
    // Interceptor handles unified structure, response.data is already unwrapped
    return response.data.folders || response.data.clipboards || [];
  },

  async createFolder(name, retentionPeriod = 7776000) {
    // retentionPeriod === null → Unlimited (subscription required, server enforces)
    // retentionPeriod omitted  → defaults to 7776000 (90 days)
    const body = { name };
    if (retentionPeriod !== undefined) body.retention_period = retentionPeriod;
    const response = await api.post('/api/clipboard', body);
    return response.data;
  },

  async updateFolder(folderId, { name, retentionPeriod }) {
    // name and retention_period are both required by the server.
    // retentionPeriod === null → Unlimited (server enforces subscription check)
    const response = await api.put(`/api/clipboard/${folderId}`, {
      name,
      retention_period: retentionPeriod,
    });
    return response.data;
  },

  async getFolder(folderId) {
    const response = await api.get(`/api/clipboard/${folderId}`);
    return response.data;
  },

  async deleteFolder(folderId) {
    await api.delete(`/api/clipboard/${folderId}`);
  },

  async getFolderItems(folderId, options = {}) {
    // Always use the new unified endpoint with optional folder query parameter
    const params = folderId ? { folder: folderId } : {};
    const response = await api.get('/api/items', {
      params,
      signal: options.signal,
    });
    // Interceptor handles unified structure, response.data is already unwrapped
    const rawItems = response.data.items || [];
    
    // Transform raw API data to domain models
    return rawItems.map(createItem);
  },

  async getFolderItemsWithContent(folderId, options = {}) {
    // Get items using existing logic
    const items = await this.getFolderItems(folderId, options);

    try {
      // Open cache — requires a secure context (HTTPS/localhost); skip preloading if unavailable
      const cache = await getContentCache();

      // Preload content for all items in parallel
      await Promise.allSettled(
        items.map(item => preloadItemContent(item, cache))
      );
    } catch (error) {
      console.warn('Content preloading skipped (Cache API unavailable):', error.message);
    }

    return items;
  },

  async createItem(folderId, blob) {
    // Upload file to storage
    const { fileId } = await storage.uploadFile(blob);

    const response = await api.post('/api/items', {
      content_type: blob.type,
      file_id: fileId,
      clipboard_id: folderId,
    });

    return createItem(response.data, ClipboardItem.STATUS_PERSISTED);
  },

  async updateItem(folderId, itemId, itemData) {
    const response = await api.put(`/api/clipboard/${folderId}/items/${itemId}`, itemData);
    
    return createItem(response.data);
  },

  async deleteItem(itemId) {
    await api.delete(`/api/items/${itemId}`);
  },

  async trashItem(itemId) {
    await api.post(`/api/items/${itemId}/trash`);
  },

  async getTrashedItems(options = {}) {
    const response = await api.get('/api/items/trash', {
      signal: options.signal,
    });
    // Interceptor handles unified structure, response.data is already unwrapped
    const rawItems = response.data.items || [];
    return rawItems.map(createItem);
  },

  async restoreItem(itemId) {
    await api.post(`/api/items/${itemId}/restore`);
  },

  async moveItem(itemId, targetFolderId) {
    await api.put(`/api/items/${itemId}/move`, { clipboard_id: targetFolderId });
  },

  async pinItem(itemId, pinned) {
    const response = await api.patch(`/api/items/${itemId}/pin`, { pinned });
    return response.data;
  },

  async bulkDeleteItems(itemIds) {
    const response = await api.post('/api/items/bulk-delete', { item_ids: itemIds });
    return response.data;
  },

  async bulkMoveItems(itemIds, targetFolderId) {
    const response = await api.post('/api/items/bulk-move', { item_ids: itemIds, clipboard_id: targetFolderId });
    return response.data;
  },
};
